/**
 * Park Manager Portal - Jobs Page
 * Mobile-first jobs list with filters and job detail view
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Search, 
  Filter, 
  ClipboardList,
  Calendar,
  User,
  MessageSquare,
  Paperclip,
  Camera,
  X,
  ChevronLeft,
  Send,
  Loader2
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { PortalLayout } from '../components/PortalLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Wyldecrest Stabilised Workflow - 8 Stages Only
const STATUS_COLORS = {
  'Job Created': 'bg-blue-100 text-blue-700',
  'Awaiting Quote': 'bg-purple-100 text-purple-700',
  'Quote Awaiting Approval': 'bg-amber-100 text-amber-700',
  'Approved': 'bg-green-100 text-green-700',
  'Assigned': 'bg-cyan-100 text-cyan-700',
  'Scheduled': 'bg-teal-100 text-teal-700',
  'Completed': 'bg-emerald-100 text-emerald-700',
  'Closed': 'bg-slate-100 text-slate-600',
};

const PRIORITY_COLORS = {
  'Urgent': 'bg-red-600 text-white',
  'High': 'bg-orange-500 text-white',
  'Medium': 'bg-blue-500 text-white',
  'Low': 'bg-slate-400 text-white',
};

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'Pending Quote', label: 'Pending Quote' },
  { value: 'Awaiting Approval', label: 'Awaiting Approval' },
  { value: 'PO Issued', label: 'PO Issued' },
  { value: 'Scheduled', label: 'Scheduled' },
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Completed', label: 'Completed' },
];

export default function PortalJobsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobDetail, setShowJobDetail] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Note form
  const [noteText, setNoteText] = useState('');
  const [submittingNote, setSubmittingNote] = useState(false);

  const fetchJobs = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/park-portal/jobs?limit=100`;
      
      if (statusFilter && statusFilter !== 'all') {
        url += `&status=${encodeURIComponent(statusFilter)}`;
      }
      if (priorityFilter && priorityFilter !== 'all') {
        url += `&priority=${encodeURIComponent(priorityFilter)}`;
      }
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to fetch jobs');
      const data = await res.json();
      setJobs(data);
      
      // Check for selected job from URL
      const selectedId = searchParams.get('selected');
      if (selectedId) {
        const job = data.find(j => j.id === selectedId);
        if (job) {
          fetchJobDetail(selectedId);
        }
      }
    } catch (error) {
      toast.error('Failed to load jobs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobDetail = async (jobId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/park-portal/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to fetch job details');
      const data = await res.json();
      setSelectedJob(data);
      setShowJobDetail(true);
    } catch (error) {
      toast.error('Failed to load job details');
      console.error(error);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim() || !selectedJob) return;
    
    setSubmittingNote(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/park-portal/jobs/${selectedJob.id}/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ note: noteText })
      });
      
      if (!res.ok) throw new Error('Failed to add note');
      
      const data = await res.json();
      setSelectedJob({
        ...selectedJob,
        notes: [...(selectedJob.notes || []), data.note]
      });
      setNoteText('');
      toast.success('Note added');
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setSubmittingNote(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !selectedJob) return;
    
    const token = localStorage.getItem('token');
    
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const res = await fetch(`${API_URL}/api/park-portal/jobs/${selectedJob.id}/attachments`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });
        
        if (res.ok) {
          const data = await res.json();
          setSelectedJob({
            ...selectedJob,
            attachments: [...(selectedJob.attachments || []), data.attachment]
          });
        }
      } catch (error) {
        console.error('Upload failed:', error);
      }
    }
    
    toast.success('Photos uploaded');
  };

  useEffect(() => {
    fetchJobs();
  }, [statusFilter, priorityFilter]);

  // Filter jobs by search
  const filteredJobs = jobs.filter(job => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (job.job_reference || '').toLowerCase().includes(q) ||
      (job.description || '').toLowerCase().includes(q) ||
      (job.unit_pitch || '').toLowerCase().includes(q) ||
      (job.job_type_label || '').toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <PortalLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F7A78]"></div>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div className="p-4 pb-20 md:p-6 md:pb-6">
        {/* Header */}
        <div className="mb-4">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 flex items-center gap-2">
            <ClipboardList className="h-6 w-6 text-[#1F7A78]" />
            Jobs
          </h1>
          <p className="text-sm text-slate-500 mt-1">{filteredJobs.length} jobs found</p>
        </div>

        {/* Search & Filter */}
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search jobs..."
              className="pl-9"
              data-testid="search-jobs"
            />
          </div>
          <Button 
            variant="outline" 
            onClick={() => setShowFilters(!showFilters)}
            className={showFilters ? 'bg-slate-100' : ''}
          >
            <Filter className="h-4 w-4" />
          </Button>
        </div>

        {/* Filters */}
        {showFilters && (
          <Card className="border-0 shadow-sm mb-4">
            <CardContent className="p-4 space-y-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Status</label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Priority</label>
                <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priorities</SelectItem>
                    <SelectItem value="Urgent">Urgent</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => { setStatusFilter('all'); setPriorityFilter('all'); }}
              >
                Clear Filters
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Jobs List */}
        <div className="space-y-2">
          {filteredJobs.length > 0 ? (
            filteredJobs.map((job) => (
              <Card 
                key={job.id}
                className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => fetchJobDetail(job.id)}
                data-testid={`job-card-${job.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{job.job_reference}</span>
                    <Badge className={`text-xs ${STATUS_COLORS[job.status] || 'bg-slate-100'}`}>
                      {job.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 line-clamp-2 mb-2">
                    {job.description || job.job_type_label}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    {job.unit_pitch && (
                      <span className="bg-slate-100 px-2 py-1 rounded">{job.unit_pitch}</span>
                    )}
                    <Badge className={`${PRIORITY_COLORS[job.priority] || 'bg-slate-400 text-white'}`}>
                      {job.priority}
                    </Badge>
                    {job.scheduled_date && (
                      <span className="text-slate-500 flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {format(new Date(job.scheduled_date), 'dd MMM')}
                      </span>
                    )}
                    {job.contractor_name && (
                      <span className="text-slate-500 flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {job.contractor_name}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="text-center py-12 text-slate-500">
              <ClipboardList className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No jobs found</p>
              <p className="text-sm">Try adjusting your filters</p>
            </div>
          )}
        </div>
      </div>

      {/* Job Detail Dialog */}
      <Dialog open={showJobDetail} onOpenChange={setShowJobDetail}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-0">
          {selectedJob && (
            <>
              <DialogHeader className="p-4 pb-0">
                <div className="flex items-center gap-2 mb-2">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowJobDetail(false)}
                    className="h-8 w-8 p-0"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <DialogTitle className="flex-1">{selectedJob.job_reference}</DialogTitle>
                  <Badge className={`${STATUS_COLORS[selectedJob.status] || 'bg-slate-100'}`}>
                    {selectedJob.status}
                  </Badge>
                </div>
              </DialogHeader>
              
              <div className="p-4 space-y-4">
                {/* Job Info */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-slate-500">Unit/Pitch</p>
                    <p className="font-medium">{selectedJob.unit_pitch || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Priority</p>
                    <Badge className={`${PRIORITY_COLORS[selectedJob.priority]}`}>
                      {selectedJob.priority}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-slate-500">Issue Type</p>
                    <p className="font-medium">{selectedJob.job_type_label || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Created</p>
                    <p className="font-medium">
                      {selectedJob.created_at ? format(new Date(selectedJob.created_at), 'dd MMM yyyy') : '-'}
                    </p>
                  </div>
                  {selectedJob.contractor_name && (
                    <>
                      <div>
                        <p className="text-slate-500">Contractor</p>
                        <p className="font-medium">{selectedJob.contractor_name}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Scheduled</p>
                        <p className="font-medium">
                          {selectedJob.scheduled_date 
                            ? `${format(new Date(selectedJob.scheduled_date), 'dd MMM yyyy')}${selectedJob.scheduled_time ? ` ${selectedJob.scheduled_time}` : ''}`
                            : 'Not scheduled'
                          }
                        </p>
                      </div>
                    </>
                  )}
                </div>

                {/* Description */}
                <div>
                  <p className="text-sm text-slate-500 mb-1">Description</p>
                  <p className="text-sm bg-slate-50 p-3 rounded-lg">{selectedJob.description || 'No description'}</p>
                </div>

                {/* Completion Notes */}
                {selectedJob.completion_notes && (
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Completion Notes</p>
                    <p className="text-sm bg-green-50 p-3 rounded-lg border border-green-200">
                      {selectedJob.completion_notes}
                    </p>
                  </div>
                )}

                {/* Attachments */}
                {selectedJob.attachments?.length > 0 && (
                  <div>
                    <p className="text-sm text-slate-500 mb-2 flex items-center gap-1">
                      <Paperclip className="h-3 w-3" />
                      Attachments ({selectedJob.attachments.length})
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {selectedJob.attachments.map((att, idx) => (
                        <div key={idx} className="w-20 h-20 bg-slate-100 rounded-lg overflow-hidden">
                          {att.content_type?.startsWith('image/') ? (
                            <img 
                              src={`${API_URL}/uploads/portal/${att.id}${att.filename?.match(/\.[^.]+$/)?.[0] || ''}`} 
                              alt={att.filename}
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-xs text-slate-500">
                              {att.filename}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <p className="text-sm text-slate-500 mb-2 flex items-center gap-1">
                    <MessageSquare className="h-3 w-3" />
                    Notes ({selectedJob.notes?.length || 0})
                  </p>
                  <ScrollArea className="max-h-40">
                    {selectedJob.notes?.length > 0 ? (
                      <div className="space-y-2">
                        {selectedJob.notes.map((note, idx) => (
                          <div key={idx} className="bg-slate-50 p-3 rounded-lg text-sm">
                            <p>{note.text}</p>
                            <p className="text-xs text-slate-400 mt-1">
                              {note.created_by} • {note.created_at ? format(new Date(note.created_at), 'dd MMM HH:mm') : ''}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400">No notes yet</p>
                    )}
                  </ScrollArea>
                </div>

                {/* Add Note Form */}
                <div className="border-t pt-4">
                  <div className="flex gap-2">
                    <Textarea
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      placeholder="Add a note..."
                      rows={2}
                      className="flex-1"
                      data-testid="note-input"
                    />
                    <div className="flex flex-col gap-2">
                      <Button 
                        size="sm"
                        onClick={handleAddNote}
                        disabled={!noteText.trim() || submittingNote}
                        className="bg-[#1F7A78]"
                        data-testid="add-note-btn"
                      >
                        {submittingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      </Button>
                      <label className="cursor-pointer">
                        <div className="h-8 w-8 flex items-center justify-center border rounded hover:bg-slate-50">
                          <Camera className="h-4 w-4 text-slate-500" />
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={handlePhotoUpload}
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </PortalLayout>
  );
}
